import { useEffect, useRef, useState, type FormEvent } from 'react';

import type { ChallengeView } from '../types.ts';

/**
 * The states code entry can be in.
 *
 * `expired` and `locked` are separate, and `expired` is separate from a wrong answer,
 * because the advice differs in each case: retype, start again, or stop. That distinction
 * is bought at some cost on the server — `VerifyAuthChallengeResponse` checks expiry
 * before comparing the digest, and the BFF re-checks expiry before calling Cognito
 * because Cognito's own response cannot tell the two apart — so collapsing them here
 * would waste the only thing those two decisions were for.
 *
 * `delivery-failed` and `booking-changed` are terminal for *this* attempt but not for the
 * booking, which is why they get their own advice rather than sharing "something went
 * wrong". A delivery failure is worth retrying unchanged; a changed booking has to be
 * verified again from the start, because the customer is now agreeing to something else.
 */
export type ChallengeStage =
  | 'sending'
  | 'awaiting-code'
  | 'verified'
  | 'locked'
  | 'expired'
  | 'delivery-failed'
  | 'booking-changed';

interface ChallengePanelProps {
  readonly challenge: ChallengeView | undefined;
  readonly stage: ChallengeStage;
  readonly secondsRemaining: number;
  readonly otpLength: number;
  /** Configured code lifetime, so the drain bar has a denominator. */
  readonly otpTtlSeconds: number;
  readonly busy: boolean;
  /** Seconds until another delivery is permitted. Zero means it is available now. */
  readonly resendSecondsRemaining: number;
  readonly onSubmit: (code: string) => void;
  readonly onResend: () => void;
  readonly onRestart: () => void;
  readonly onCancel: () => void;
}

/**
 * Code entry, the countdown, and the terminal states.
 *
 * The panel is deliberately explicit about which of the four ways this can end has
 * happened, because each one needs different advice: retype, wait, start again, or give
 * up. Collapsing them into "verification failed" would be the same mistake as letting
 * the server return one status for all of them.
 */
export function ChallengePanel({
  challenge,
  stage,
  secondsRemaining,
  otpLength,
  otpTtlSeconds,
  busy,
  resendSecondsRemaining,
  onSubmit,
  onResend,
  onRestart,
  onCancel,
}: ChallengePanelProps) {
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Focus moves to the code field when it appears, and to the heading when the flow
  // ends. Without this a keyboard user's focus stays on the button they last pressed,
  // several steps behind what the page now says.
  useEffect(() => {
    if (stage === 'awaiting-code') inputRef.current?.focus();
    else if (stage !== 'sending') headingRef.current?.focus();
  }, [stage]);

  /**
   * A fresh attempt starts from an empty field rather than the rejected code.
   *
   * Cleared here, at the moment of submission, rather than in an effect watching
   * `attemptsRemaining`. Same visible behaviour, and it removes a `setState` inside an
   * effect body — which is a cascading render rather than a synchronisation, and is what
   * `react-hooks/set-state-in-effect` exists to catch. It also covers a case the effect
   * did not: a submission that fails before the server answers still clears, because the
   * digits in the field have been spent either way.
   */
  function submit(event: FormEvent): void {
    event.preventDefault();
    const submitted = code.trim();
    if (submitted.length === 0) return;
    setCode('');
    onSubmit(submitted);
  }

  // Fraction of the configured lifetime still available. A wrong answer does not extend
  // the window, so this keeps draining across retries — which is the behaviour the
  // server implements and therefore the behaviour the bar has to show.
  const ttlFraction = Math.max(0, Math.min(1, secondsRemaining / Math.max(1, otpTtlSeconds)));

  const recovery = RECOVERY[stage];

  /**
   * Ask for the code again, then put focus back in the field.
   *
   * The focus move is not a nicety. A successful resend disables this button for the
   * cooldown, and a disabled control cannot hold focus, so a keyboard-only user would be
   * dropped onto `<body>` at the exact moment a new code is arriving. The field is also
   * where they are going next, so this is the destination they would have chosen.
   */
  function requestResend(): void {
    onResend();
    inputRef.current?.focus();
  }

  return (
    <section
      className={`challenge${stage === 'verified' ? ' challenge--sealed' : ''}`}
      aria-labelledby="challenge-heading"
      data-testid="challenge-panel"
      data-stage={stage}
    >
      <p className="eyebrow">Step 2 of 3 · verification</p>
      <h2 id="challenge-heading" ref={headingRef} tabIndex={-1}>
        {headingFor(stage, challenge)}
      </h2>

      <ol className="steps">
        {/*
          A delivery failure must not tick "code sent". The whole point of that state is
          that nothing was sent, and a progress indicator that says otherwise is the one
          place a demo audience would reasonably stop trusting the screen.
        */}
        <li data-done={stage !== 'sending' && stage !== 'delivery-failed'}>code sent</li>
        <li
          aria-current={stage === 'awaiting-code' ? 'step' : undefined}
          data-done={stage === 'verified'}
        >
          code entered
        </li>
        <li data-done={stage === 'verified'}>booking confirmed</li>
      </ol>

      {stage === 'sending' ? <p className="muted">Sending a code…</p> : null}

      {stage === 'awaiting-code' && challenge ? (
        <>
          <p>
            Enter the {otpLength}-digit code sent to{' '}
            <span className="value">{challenge.maskedEmail}</span>.
            {challenge.delivered ? null : ' A code was already on its way, so no new one was sent.'}
          </p>

          <form onSubmit={submit} noValidate>
            <div className="code-entry">
              <div>
                <label htmlFor="otp">Verification code</label>
                <input
                  id="otp"
                  ref={inputRef}
                  className="input--code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  // `maxLength` is a convenience, not a control: the server validates
                  // the shape and the code length is configurable.
                  maxLength={otpLength}
                  pattern={`[0-9]{${otpLength}}`}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replaceAll(/\s/g, ''))}
                  aria-describedby="otp-help"
                />
              </div>
              <button type="submit" disabled={busy || code.length === 0}>
                {busy ? 'Checking\u2026' : 'Confirm booking'}
              </button>
              <button type="button" className="button--quiet" onClick={onCancel} disabled={busy}>
                Not now
              </button>
            </div>

            <p id="otp-help" className="muted">
              <span className="value">{challenge.attemptsRemaining}</span> attempt
              {challenge.attemptsRemaining === 1 ? '' : 's'} left · expires in{' '}
              <span className="value">{formatSeconds(secondsRemaining)}</span>
            </p>
          </form>

          <div className={`ttl${secondsRemaining <= 30 ? ' ttl--urgent' : ''}`}>
            <div className="ttl__track">
              <div
                className="ttl__fill"
                style={{ width: `${Math.round(ttlFraction * 100)}%` }}
                role="presentation"
              />
            </div>
          </div>

          {/*
            The resend control sits after the form rather than inside it, which keeps the
            tab order of the primary path — field, confirm, step away — unchanged, and puts
            the secondary action after the thing it is secondary to.

            The help text is bound with `aria-describedby` rather than placed in a live
            region. It carries a per-second countdown, and announcing that every second
            would make the panel unusable with a screen reader; read on focus is when a
            customer actually wants it. The confirmation that a code was sent goes through
            the page's one live region instead, which the parent owns.
          */}
          <p className="resend">
            <button
              type="button"
              className="button--quiet"
              onClick={requestResend}
              disabled={busy || challenge.resendsRemaining === 0 || resendSecondsRemaining > 0}
              aria-describedby="resend-help"
              data-testid="resend-button"
            >
              Send another code
            </button>{' '}
            <span id="resend-help" className="muted" data-testid="resend-help">
              {resendHelp(challenge.resendsRemaining, resendSecondsRemaining)}
            </span>
          </p>
        </>
      ) : null}

      {recovery ? (
        <>
          <p data-testid="challenge-advice">{recovery.detail}</p>
          <div className="actions">
            <button type="button" onClick={onRestart} disabled={busy}>
              {recovery.restartLabel}
            </button>
            <button type="button" className="button--quiet" onClick={onCancel} disabled={busy}>
              Back to bookings
            </button>
          </div>
        </>
      ) : null}

      {stage === 'verified' ? <p>Verified. Confirming your booking…</p> : null}
    </section>
  );
}

interface Recovery {
  readonly detail: string;
  /** The primary action's label. Naming the *next* step, not the failure. */
  readonly restartLabel: string;
}

/**
 * What to say, and what the primary button does, for each way this can end badly.
 *
 * Every one of these states leaves the booking untouched, and every one of them says so.
 * A customer who has just been refused wants to know what happened to their money before
 * they want to know what to press.
 */
const RECOVERY: Readonly<Record<string, Recovery>> = {
  locked: {
    detail:
      'That was the last attempt. The verification is closed, your booking has not changed and no charge was made.',
    restartLabel: 'Start verification again',
  },
  expired: {
    detail:
      'That code is no longer valid, so entering it again will not work. Request a new one — your booking has not changed.',
    restartLabel: 'Send a new code',
  },
  'delivery-failed': {
    detail:
      'We could not send the code, so there is nothing to enter yet. Your booking has not changed and it has not been confirmed — the confirmation is refused rather than allowed through, because a code that never arrived cannot have been checked.',
    restartLabel: 'Try sending it again',
  },
  'booking-changed': {
    detail:
      'This booking changed after verification was requested, so the details you were asked to confirm are no longer the details on file. Verifying again covers the new amount.',
    restartLabel: 'Verify the new details',
  },
};

/**
 * What to say beside the resend button.
 *
 * Three states, and the middle one is the one worth getting right: a customer told only
 * "please wait" presses the button repeatedly. Naming the wait turns a refusal into an
 * instruction.
 *
 * The last sentence is the honest part. A resend delivers a *new* code, because the only
 * component permitted to send email is the Cognito trigger and the only way to reach it is
 * a fresh challenge — so the code already in the inbox stops working. Saying so is cheaper
 * than a customer typing the older of two codes and spending an attempt on it.
 */
export function resendHelp(resendsRemaining: number, secondsRemaining: number): string {
  if (resendsRemaining === 0) {
    return 'No more codes can be sent for this verification.';
  }
  if (secondsRemaining > 0) {
    return `You can request another code in ${formatSeconds(secondsRemaining)}.`;
  }
  return `Not arrived? A new code replaces the one you have. ${resendsRemaining} left, and asking does not use an attempt.`;
}

function headingFor(stage: ChallengeStage, challenge: ChallengeView | undefined): string {
  switch (stage) {
    case 'sending':
      return 'Sending your code';
    case 'awaiting-code':
      return challenge?.delivered === false ? 'Enter the code you were sent' : 'Check your email';
    case 'verified':
      return 'Verified';
    case 'locked':
      return 'Too many incorrect attempts';
    case 'delivery-failed':
      return 'We could not send your code';
    case 'booking-changed':
      return 'This booking changed';
    case 'expired':
    default:
      return 'That code expired';
  }
}

function formatSeconds(total: number): string {
  const safe = Math.max(0, total);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
